/**
 * Der Zugriff auf die Schnittstelle.
 *
 * Alles laeuft hier durch -- kein `fetch` in einer Komponente. Das haelt vier
 * Dinge an einer Stelle zusammen, die sonst an vielen Stellen halb richtig
 * waeren:
 *
 * 1. Das Zugangstoken wandert in den `Authorization`-Kopf.
 * 2. Fehlerantworten werden in :class:`ApiError` uebersetzt, mit `code` und
 *    Vorgangskennung.
 * 3. Bei 401 wird die Sitzung **einmal** erneuert und der Aufruf wiederholt.
 *    Einmal, nicht in einer Schleife: Zwei aufeinanderfolgende 401 heissen,
 *    dass die Sitzung wirklich zu Ende ist.
 * 4. `credentials: 'include'` -- ohne diese Angabe sendet der Browser das
 *    `httpOnly`-Cookie mit dem Erneuerungstoken nicht mit, und die Erneuerung
 *    scheiterte immer.
 *
 * Die Typen stammen aus `src/api/schema.ts`, das aus dem OpenAPI-Schema
 * erzeugt wird (docs/02, Abschnitt 4). Damit brechen Typfehler beim Build und
 * nicht in Produktion.
 */

import { ApiError, NetworkError, type ProblemDetails } from './apiError';
import { clearSession, getAccessToken, getSession, isExpiringSoon, setSession } from './session';

export const API_BASE = '/api/v1';

const NO_REFRESH_PATHS = new Set(['/auth/login', '/auth/refresh']);

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Form-kodiert statt JSON -- der OAuth2-Anmeldeendpunkt verlangt das. */
  form?: Record<string, string>;
  headers?: Record<string, string>;
  query?: Record<string, string | number | boolean | undefined | null>;
  /**
   * Ohne das Zugangstoken der Sitzung aufrufen.
   *
   * Fuer die oeffentlichen Endpunkte des Vorgangszugangs (F4-35). Sie sind
   * ohne Anmeldung erreichbar, pruefen ein mitgeschicktes Token aber
   * trotzdem -- und ein abgelaufenes ergaebe dort ein 401 auf einen
   * Endpunkt, der gar keine Anmeldung verlangt. Wer aus einer Mail auf den
   * Link klickt, sitzt genau in diesem Fall: Er war vor Wochen einmal im
   * Portal angemeldet.
   */
  withoutSession?: boolean;
  /** Intern: verhindert eine zweite Erneuerung nach einem erneuten 401. */
  isRetry?: boolean;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const {
    method = 'GET',
    body,
    form,
    headers = {},
    query,
    withoutSession = false,
    isRetry = false,
  } = options;

  const url = new URL(`${API_BASE}${path}`, window.location.origin);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.set(key, String(value));
      }
    }
  }

  // **Erneuern, bevor es zu spaet ist.**
  //
  // Das Zugangstoken gilt Minuten, die Anmeldekette vierzehn Tage. Ohne
  // diese Zeilen merkt die Oberflaeche das Ablaufen erst am 401 -- der
  // Aufruf wird dann zwar wiederholt, aber jeder solche Fall ist eine
  // zusaetzliche Runde, und bei mehreren gleichzeitigen Aufrufen laufen
  // mehrere davon nebeneinander.
  //
  // Nicht bei `isRetry`: Dort kommen wir gerade aus einer Erneuerung.
  // Nicht bei den Anmeldewegen selbst: Sie haben noch keine Sitzung oder
  // sind die Erneuerung.
  const current = withoutSession ? null : getSession();
  if (current && !isRetry && !NO_REFRESH_PATHS.has(path) && isExpiringSoon(current)) {
    await tryRefresh();
  }

  const requestHeaders: Record<string, string> = { Accept: 'application/json', ...headers };
  const token = withoutSession ? null : getAccessToken();
  if (token) requestHeaders.Authorization = `Bearer ${token}`;

  let payload: BodyInit | undefined;
  if (form) {
    requestHeaders['Content-Type'] = 'application/x-www-form-urlencoded';
    payload = new URLSearchParams(form).toString();
  } else if (body !== undefined) {
    requestHeaders['Content-Type'] = 'application/json';
    payload = JSON.stringify(body);
  }

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      headers: requestHeaders,
      body: payload,
      // Ohne diese Zeile bleibt das Cookie mit dem Erneuerungstoken zu Hause.
      credentials: 'include',
    });
  } catch (cause) {
    throw new NetworkError(cause);
  }

  // Ein 401 von den Anmeldeendpunkten selbst wird nicht mit einer Erneuerung
  // beantwortet. Bei `/auth/login` heisst 401 schlicht "Zugangsdaten falsch";
  // eine Erneuerung waere sinnlos und braechte dem Anwender nur eine
  // Verzoegerung. Bei `/auth/refresh` entstuende eine Endlosschleife.
  // Ein 401 von einem Aufruf ohne Sitzung ist keine abgelaufene Sitzung,
  // sondern eine abgewiesene Angabe. Eine Erneuerung braechte dieselbe
  // Antwort noch einmal -- und meldete im Fehlerfall eine Sitzung ab, die
  // mit diesem Aufruf nichts zu tun hat.
  if (response.status === 401 && !withoutSession && !isRetry && !NO_REFRESH_PATHS.has(path)) {
    const outcome = await refreshOnce();
    if (outcome === 'ok') {
      return request<T>(path, { ...options, isRetry: true });
    }
    // **Nur bei einer Absage abmelden, nicht bei einem Netzausfall.**
    // `unreachable` heisst: Wir wissen nicht, ob die Sitzung noch gilt --
    // und im Zweifel wirft man den Anwender nicht hinaus. Er sieht den
    // Netzfehler und kann es noch einmal versuchen; seine Sitzung steht
    // noch, wenn die Verbindung wieder steht.
    if (outcome === 'expired') clearSession();
  }

  if (!response.ok) {
    throw new ApiError(await readProblem(response), response.status);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

/**
 * Laedt eine Datei hoch.
 *
 * Eigener Weg statt eines Sonderfalls in `request`: Bei `FormData` darf der
 * `Content-Type` **nicht** gesetzt werden -- der Browser ergaenzt ihn samt
 * Trennzeichen selbst, und ein von Hand gesetzter Kopf macht den Rumpf
 * unlesbar. Diese Regel in `request` unterzubringen hiesse, sie bei jeder
 * spaeteren Aenderung erneut zu beachten.
 */
export async function upload<T>(path: string, file: File, fieldName = 'file'): Promise<T> {
  const form = new FormData();
  form.append(fieldName, file);

  const url = new URL(`${API_BASE}${path}`, window.location.origin);
  const headers: Record<string, string> = { Accept: 'application/json' };
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  let response: Response;
  try {
    response = await fetch(url, { method: 'POST', headers, body: form, credentials: 'include' });
  } catch (cause) {
    throw new NetworkError(cause);
  }

  if (!response.ok) {
    throw new ApiError(await readProblem(response), response.status);
  }
  return (await response.json()) as T;
}

/**
 * Holt eine Datei als Blob -- Anhang oder Ausdruck.
 *
 * Die Antwort wird ueber `fetch` geholt und nicht ueber ein `<a href>`: Ein
 * einfacher Verweis traegt den `Authorization`-Kopf nicht, und ein Token in
 * der Adresszeile stuende im Verlauf des Browsers und in jedem Protokoll
 * dazwischenliegender Server.
 */
export async function download(path: string): Promise<{ blob: Blob; filename: string | null }> {
  const url = new URL(`${API_BASE}${path}`, window.location.origin);
  const headers: Record<string, string> = {};
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  let response: Response;
  try {
    response = await fetch(url, { headers, credentials: 'include' });
  } catch (cause) {
    throw new NetworkError(cause);
  }

  if (!response.ok) {
    throw new ApiError(await readProblem(response), response.status);
  }

  return { blob: await response.blob(), filename: filenameFrom(response) };
}

/**
 * Liest den Dateinamen aus `Content-Disposition`.
 *
 * Nur die einfache Form; der Server schickt ausschliesslich diese. Ohne
 * Treffer bleibt der Name offen und der Aufrufer setzt einen eigenen -- ein
 * geratener Name waere schlechter als gar keiner.
 */
function filenameFrom(response: Response): string | null {
  const header = response.headers.get('content-disposition') ?? '';
  const match = /filename="([^"]+)"/.exec(header);
  return match?.[1] ?? null;
}

async function readProblem(response: Response): Promise<ProblemDetails | null> {
  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('json')) return null;
  try {
    return (await response.json()) as ProblemDetails;
  } catch {
    // Eine Fehlerantwort ohne lesbaren Rumpf ist selten, aber moeglich --
    // etwa wenn ein Proxy dazwischenfunkt. Der Statuscode genuegt dann.
    return null;
  }
}

interface TokenResponse {
  access_token: string;
  token_type: string;
  expires_at: string;
  tenant_id: string | null;
  requires_tenant_selection: boolean;
}

/**
 * Tauscht das Erneuerungscookie gegen ein neues Zugangstoken.
 *
 * Gleichzeitige Aufrufe teilen sich einen einzigen Versuch. Ohne diese
 * Zusammenfassung wuerden bei einem abgelaufenen Token alle gerade laufenden
 * Abfragen zugleich erneuern -- und weil der Server die Token rotiert und
 * eine Wiedervorlage als Diebstahl wertet, wuerde er die Sitzung darauf
 * beenden. Ein gleichzeitiges Nachladen mehrerer Listen wuerde den Nutzer
 * also zuverlaessig abmelden.
 *
 * **Ueber Reiter hinweg traegt diese Zusammenfassung nicht.** Jeder Reiter
 * hat seinen eigenen Arbeitsspeicher und damit seinen eigenen Merker; das
 * Cookie teilen sich alle. Zwei Reiter, die im selben Augenblick erneuern,
 * legen deshalb dasselbe Token vor. Dagegen hilft nichts im Browser,
 * sondern nur der Server: Er laesst ein gerade gedrehtes Token noch kurz
 * gelten (`refresh_grace_seconds`) und unterscheidet damit Gleichzeitigkeit
 * von Diebstahl.
 */
let pendingRefresh: Promise<RefreshOutcome> | null = null;

/**
 * Das Ergebnis einer Erneuerung.
 *
 * Die Unterscheidung zwischen `expired` und `unreachable` ist der Grund fuer
 * diesen Typ: Beim ersten ist die Sitzung zu Ende, beim zweiten wissen wir
 * es nicht. Frueher standen beide als `false` da, und ein Funkloch meldete
 * den Anwender ab.
 */
type RefreshOutcome = 'ok' | 'expired' | 'unreachable';

export function tryRefresh(): Promise<boolean> {
  return refreshOnce().then((outcome) => outcome === 'ok');
}

function refreshOnce(): Promise<RefreshOutcome> {
  pendingRefresh ??= performRefresh().finally(() => {
    pendingRefresh = null;
  });
  return pendingRefresh;
}

async function performRefresh(): Promise<RefreshOutcome> {
  try {
    const tokens = await request<TokenResponse>('/auth/refresh', {
      method: 'POST',
      isRetry: true,
    });
    applyTokenResponse(tokens);
    return 'ok';
  } catch (error) {
    return error instanceof NetworkError ? 'unreachable' : 'expired';
  }
}

export function applyTokenResponse(tokens: TokenResponse): void {
  setSession({
    accessToken: tokens.access_token,
    expiresAt: new Date(tokens.expires_at),
    tenantId: tokens.tenant_id,
    requiresTenantSelection: tokens.requires_tenant_selection,
  });
}

export type { TokenResponse };
